import classNames from 'classnames';
import * as _ from 'lodash-es';
import React from 'react';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import * as styles from './Avatar.module.scss';

const THE_GOLDEN_RATIO = 1.61803398875;

const NUM_AVATAR_COLORS = 6;

gql(`
  fragment Avatar_avatar on Image {
    thumb
  }
`);

const USER_FRAGMENT = gql(`
  fragment Avatar_user on UserFields {
    id
    avatar {
      ...Avatar_avatar
    }
    name
  }
`);

const INVITATION_FRAGMENT = gql(`
  fragment Avatar_invitation on Invitation {
    id
    avatar {
      ...Avatar_avatar
    }
    name
  }
`);

type AvatarProps = {
  muted?: boolean;
  size?: ('small' | 'regular' | 'medium' | 'large') | number;
} & (
  | { skeleton: true; user?: never }
  | {
      skeleton?: false;
      user:
        | DocumentType<typeof USER_FRAGMENT>
        | DocumentType<typeof INVITATION_FRAGMENT>;
    }
);

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getInitials = (name = '') => {
  const letters = name
    .split(' ')
    .filter((token) => token)
    .map((token) => _.split(token, '')[0]?.toUpperCase());
  letters.splice(1, letters.length - 2);

  return letters.join('');
};

export const getAvatarColorSequence = (name: string) => {
  return (
    _.split(name, '').reduce(
      (memo, char) => (memo + char.charCodeAt(0)) % NUM_AVATAR_COLORS,
      0,
    ) + 1
  );
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const getAvatarColorClassName = (name: string) => {
  return styles[`color${getAvatarColorSequence(name)}`];
};

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ muted = false, size = 'regular', user, skeleton, ...props }, ref) => {
    const baseClasses = classNames(styles.avatar, {
      [styles.muted]: muted,
      [styles.large]: size === 'large',
      [styles.medium]: size === 'medium',
      [styles.small]: size === 'small',
    });

    if (!user && skeleton) {
      return <div className={classNames(baseClasses, styles.skeleton)} />;
    }

    if (user.name == null) return null;

    const thumb =
      typeof user.avatar === 'string' ? user.avatar : user.avatar?.thumb;

    const classes = classNames(baseClasses, {
      [getAvatarColorClassName(user.name)]: !thumb,
    });

    const style =
      typeof size === 'number'
        ? {
            fontSize: Math.max(
              Math.min(size - 6, 10),
              size / (1 + THE_GOLDEN_RATIO),
            ),
            height: size,
            width: size,
          }
        : undefined;

    return (
      <div
        ref={ref}
        style={style}
        role="img"
        aria-label={user.name}
        {...props}
        className={classes}
      >
        {thumb ? <img src={thumb} alt={user.name} /> : getInitials(user.name)}
      </div>
    );
  },
);

export default Avatar;
